import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchPolicies, fetchRecommendations, submitFeedback, generatePolicyTemplate, getToken, BASE, fetchSectors } from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import PartnerLogos from "../components/PartnerBar";
import { Sparkles, ExternalLink, Copy, ThumbsUp, ThumbsDown, Target, Shield, Search, FileDown } from "lucide-react";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import { jsPDF } from "jspdf";

const RECOMMEND_LOADING_STEPS = [
  "Computing semantic embeddings...",
  "Scanning jurisdictions for coverage gaps...",
  "Running multi-factor gap analysis...",
  "Scoring sector, maturity, and peer factors...",
  "Ranking countries by adoption priority...",
  "Finalizing recommendations..."
];

export default function Recommend() {
  const nav = useNavigate();
  const [policies,      setPolicies]      = useState([]);
  const [selected,      setSelected]      = useState(null);
  const [result,        setResult]        = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [sector,        setSector]        = useState("");
  const [search,        setSearch]        = useState("");
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const [sectorsList,   setSectorsList]   = useState([]);
  const [weights,       setWeights]       = useState({
    sector_gap: 35,
    regulatory_maturity: 25,
    semantic_need: 20,
    regional_pressure: 12,
    economic_tier: 8
  });
  
  const [step, setStep] = useState(1); // step 1: selecting policy, step 2: viewing results
  const [showWeightsPanel, setShowWeightsPanel] = useState(true); // CollapsibleFactor weights
  const [expandedReasons, setExpandedReasons] = useState({});
  const [drawerOpen,    setDrawerOpen]    = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [proposal,      setProposal]      = useState(null);

  // Hover states tracking for custom micro-interactions
  const [hoveredSector, setHoveredSector] = useState(null);
  const [hoveredPolicy, setHoveredPolicy] = useState(null);
  const [hoveredProposalBtn, setHoveredProposalBtn] = useState({});
  const [hoveredFeedbackBtn, setHoveredFeedbackBtn] = useState({});
  const [hoveredBack, setHoveredBack] = useState(false);
  const [recLoadingStep, setRecLoadingStep] = useState(0);

  const toggleReason = (country) => {
    setExpandedReasons(prev => ({
      ...prev,
      [country]: !prev[country]
    }));
  };

  const handleGenerateProposal = (country, sector) => {
    setDrawerOpen(true);
    setDrawerLoading(true);
    setProposal(null);
    generatePolicyTemplate(country, sector)
      .then(data => {
        setProposal(data);
        setDrawerLoading(false);
      })
      .catch(err => {
        console.error(err);
        alert("Failed to generate AI proposal. Please make sure you are logged in.");
        setDrawerLoading(false);
        setDrawerOpen(false);
      });
  };

  useEffect(() => {
    fetchPolicies({}).then(d => setPolicies(d || []));
    fetchSectors().then(d => d && setSectorsList(d));
  }, []);

  // Cycle through loading step messages while loading
  useEffect(() => {
    let interval;
    if (loading) {
      setRecLoadingStep(0);
      interval = setInterval(() => {
        setRecLoadingStep(s => (s < RECOMMEND_LOADING_STEPS.length - 1 ? s + 1 : s));
      }, 3000);
    } else {
      setRecLoadingStep(0);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [loading]);

  const handleSelect = (policy) => {
    setSelected(policy);
    setResult(null);
    setFeedbackGiven({});
    setLoading(true);
    setStep(2); // Switch to single column Mode B results page

    const decimalWeights = {
      sector_gap: weights.sector_gap / 100,
      regulatory_maturity: weights.regulatory_maturity / 100,
      semantic_need: weights.semantic_need / 100,
      regional_pressure: weights.regional_pressure / 100,
      economic_tier: weights.economic_tier / 100,
    };

    fetchRecommendations(policy.id, 6, decimalWeights).then(r => {
      setResult(r);
      setLoading(false);
    });
  };

  const handleWeightChange = (key, value) => {
    setWeights(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleResetWeights = () => {
    setWeights({
      sector_gap: 35,
      regulatory_maturity: 25,
      semantic_need: 20,
      regional_pressure: 12,
      economic_tier: 8
    });
  };

  const handleAutoBalance = () => {
    const sum = Object.values(weights).reduce((a, b) => a + b, 0);
    if (sum === 0) {
      setWeights({
        sector_gap: 35,
        regulatory_maturity: 25,
        semantic_need: 20,
        regional_pressure: 12,
        economic_tier: 8
      });
      return;
    }
    const factor = 100 / sum;
    const balanced = {
      sector_gap: Math.round(weights.sector_gap * factor),
      regulatory_maturity: Math.round(weights.regulatory_maturity * factor),
      semantic_need: Math.round(weights.semantic_need * factor),
      regional_pressure: Math.round(weights.regional_pressure * factor),
      economic_tier: Math.round(weights.economic_tier * factor),
    };
    
    // Adjust rounding discrepancy to exact 100
    const newSum = Object.values(balanced).reduce((a, b) => a + b, 0);
    const diff = 100 - newSum;
    if (diff !== 0) {
      balanced.sector_gap += diff;
    }
    setWeights(balanced);
  };

  const handleRecalculate = () => {
    if (!selected) return;
    setLoading(true);
    const decimalWeights = {
      sector_gap: weights.sector_gap / 100,
      regulatory_maturity: weights.regulatory_maturity / 100,
      semantic_need: weights.semantic_need / 100,
      regional_pressure: weights.regional_pressure / 100,
      economic_tier: weights.economic_tier / 100,
    };
    fetchRecommendations(selected.id, 6, decimalWeights).then(r => {
      setResult(r);
      setLoading(false);
    });
  };

  const handleFeedback = async (country, helpful) => {
    if (!result?.source_policy?.id) return;
    await submitFeedback(result.source_policy.id, country, helpful);
    setFeedbackGiven(prev => ({
      ...prev,
      [country]: helpful ? "positive" : "negative"
    }));
  };

  const handleDownloadRecsPDF = () => {
    if (!selected) return;
    const { sector_gap, regulatory_maturity, semantic_need, regional_pressure, economic_tier } = weights;
    let url = `${BASE}/recommend/download/${selected.id}?top_n=6`;
    if (sector_gap !== undefined) url += `&w_sector=${sector_gap / 100}`;
    if (regulatory_maturity !== undefined) url += `&w_maturity=${regulatory_maturity / 100}`;
    if (semantic_need !== undefined) url += `&w_semantic=${semantic_need / 100}`;
    if (regional_pressure !== undefined) url += `&w_regional=${regional_pressure / 100}`;
    if (economic_tier !== undefined) url += `&w_economic=${economic_tier / 100}`;

    window.open(url, '_blank');
  };

  const downloadProposalPDF = (proposalData) => {
    if (!proposalData || !proposalData.policy_id) return;
    const url = `${BASE}/generate/download/${proposalData.policy_id}`;
    window.open(url, '_blank');
  };

  const filtered = policies.filter(p => {
    const matchesSector = !sector || p.sector === sector;
    const matchesSearch = !search || 
      (p.title && p.title.toLowerCase().includes(search.toLowerCase())) ||
      (p.country && p.country.toLowerCase().includes(search.toLowerCase())) ||
      (p.sector && p.sector.toLowerCase().includes(search.toLowerCase()));
    return matchesSector && matchesSearch;
  });

  const weightsSum = Object.values(weights).reduce((a, b) => a + b, 0);

  return (
    <div style={{
      flex: 1,
      overflowY: "auto",
      background: "var(--bg-deep)",
      color: "var(--text-main)",
      minHeight: "100vh",
      transition: "background 0.3s ease, color 0.3s ease, border-color 0.3s ease"
    }}>
      <style>{`
        .custom-slider-input {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 4px;
          background: var(--border-lit);
          border-radius: 2px;
          outline: none;
          cursor: pointer;
          transition: background 0.3s ease;
        }
        .custom-slider-input::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: var(--text-main);
          border: 2px solid var(--bg-card);
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
          cursor: pointer;
          transition: background 0.15s ease, border-color 0.3s ease;
        }
        .custom-slider-input::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: var(--text-main);
          border: 2px solid var(--bg-card);
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
          cursor: pointer;
          transition: background 0.15s ease, border-color 0.3s ease;
        }
        .custom-slider-input:focus::-webkit-slider-thumb,
        .custom-slider-input:active::-webkit-slider-thumb {
          background: #5c9e2e !important;
        }
        .custom-slider-input:focus::-moz-range-thumb,
        .custom-slider-input:active::-moz-range-thumb {
          background: #5c9e2e !important;
        }
        .db-link-btn:hover {
          color: #5c9e2e !important;
          background: rgba(163, 230, 53, 0.1) !important;
        }
        .selected-policy-title-hover:hover {
          color: #5c9e2e !important;
        }
        .similar-policy-card-hover {
          cursor: pointer;
          transition: all 0.3s ease-in-out;
        }
        .similar-policy-card-hover:hover {
          border-color: #5c9e2e !important;
          background: var(--bg-hover) !important;
          box-shadow: 0 4px 12px rgba(92, 158, 46, 0.08);
          transform: translateY(-2px);
        }
      `}</style>

      <div className="page-container-sm">
        
        {/* ========================================================================= */}
        {/* MODE A: POLICY SELECTION (step === 1) */}
        {/* ========================================================================= */}
        {step === 1 && (
          <>
            {/* Header */}
            <div style={{ marginBottom: "32px" }}>
              <div className="flex-toolbar" style={{ justifyContent: "space-between" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                    <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "var(--cyan)" }} />
                    <span style={{ fontSize: "12px", fontFamily: "JetBrains Mono", color: "var(--text-muted)", letterSpacing: "0.1em" }}>
                      DISCOVER / INTELLIGENCE
                    </span>
                  </div>
                  <h1 className="page-title" style={{ 
                    margin: "0 0 8px 0", 
                    letterSpacing: "-1px", 
                    lineHeight: "1.1" 
                  }}>
                    Find policy <span style={{
                      position: "relative",
                      display: "inline-block",
                      zIndex: 1
                    }}>
                      gaps.
                      <span style={{
                        position: "absolute",
                        bottom: "4px",
                        left: 0,
                        right: 0,
                        height: "45%",
                        background: "rgba(163, 230, 53, 0.45)",
                        zIndex: -1
                      }} />
                    </span>
                  </h1>
                  <p style={{ fontFamily: "DM Sans", fontSize: "15px", color: "var(--text-muted)", margin: 0 }}>
                    Select a policy to discover which countries would benefit most from adopting it
                  </p>
                </div>
                <div style={{
                  display: "flex", 
                  alignItems: "center", 
                  gap: "6px",
                  padding: "8px 16px", 
                  borderRadius: "20px",
                  background: "var(--stat-bg)",
                  border: "1px solid var(--stat-border)",
                  flexShrink: 0
                }}>
                  <Sparkles size={14} color="#5c9e2e" />
                  <span style={{ fontSize: "13px", color: "var(--stat-label)", fontFamily: "JetBrains Mono", fontWeight: 600 }}>
                    ML POWERED
                  </span>
                </div>
              </div>
            </div>

            <div className="recommend-layout">
              {/* Left Panel: Filters + Compact Policy Cards */}
              <div>
                {/* Search Input Bar */}
                <div style={{ position: "relative", marginBottom: "16px" }}>
                  <span style={{ 
                    position: "absolute", 
                    left: 14, 
                    top: "50%", 
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    display: "flex",
                    alignItems: "center",
                    color: "var(--text-muted)"
                  }}>
                    <Search size={16} />
                  </span>
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search source policies..."
                    className="search-input-custom"
                    style={{
                      width: "100%",
                      height: 40,
                      border: "1px solid var(--border)",
                      borderRadius: "6px",
                      padding: "0 12px 0 38px",
                      outline: "none",
                      fontSize: "14px",
                      fontFamily: "DM Sans",
                      background: "var(--bg-card)",
                      color: "var(--text-main)",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
                      transition: "all 0.3s ease"
                    }}
                  />
                </div>

                {/* Sector Filters */}
                <div style={{ marginBottom: "20px" }}>
                  <div style={{ 
                    fontSize: "12px", 
                    color: "var(--text-muted)", 
                    fontFamily: "JetBrains Mono", 
                    marginBottom: "12px", 
                    letterSpacing: "0.1em" 
                  }}>
                    FILTER BY SECTOR
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    {[
                      "", ...sectorsList
                    ].map(s => (
                      <button 
                        key={s} 
                        onClick={() => setSector(s)}
                        onMouseEnter={() => setHoveredSector(s)}
                        onMouseLeave={() => setHoveredSector(null)}
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          borderRadius: "6px",
                          textAlign: "left",
                          fontSize: "15px",
                          fontFamily: "DM Sans",
                          cursor: "pointer",
                          background: sector === s 
                            ? "var(--stat-bg)" 
                            : hoveredSector === s 
                              ? "var(--bg-hover)" 
                              : "transparent",
                          border: "none",
                          borderLeft: sector === s ? "2px solid var(--cyan)" : "none",
                          color: sector === s ? "var(--stat-label)" : "var(--text-muted)",
                          transition: "all 0.15s ease",
                          outline: "none",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center"
                        }}
                      >
                        <span>{s || "All Sectors"}</span>
                        <span style={{ 
                          fontFamily: "JetBrains Mono", 
                          fontSize: "12px", 
                          color: "var(--text-dim)" 
                        }}>
                          {s ? policies.filter(p => p.sector === s).length : policies.length}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

              </div>

              {/* Right Panel: Grid of Policies */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Header row */}
                <div style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "space-between", 
                  marginBottom: "16px",
                  paddingBottom: "8px",
                  borderBottom: "1px solid var(--border)"
                }}>
                  <h3 style={{ 
                    fontFamily: "DM Sans", 
                    fontSize: "14px", 
                    fontWeight: 600, 
                    color: "var(--text-muted)", 
                    margin: 0,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em"
                  }}>
                    {sector ? `${sector} Policies` : "All Source Policies"}
                  </h3>
                  <span style={{ 
                    fontSize: "11px", 
                    fontFamily: "JetBrains Mono", 
                    color: "#5c9e2e", 
                    background: "rgba(92, 158, 46, 0.08)", 
                    border: "1px solid rgba(92, 158, 46, 0.15)",
                    borderRadius: "4px", 
                    padding: "3px 8px",
                    fontWeight: 600
                  }}>
                    {filtered.length} Indexed
                  </span>
                </div>

                {filtered.length === 0 ? (
                  <div style={{
                    background: "var(--bg-card)",
                    border: "1px solid var(--border)",
                    borderRadius: "8px",
                    padding: "48px",
                    textAlign: "center"
                  }}>
                    <Target size={36} color="var(--text-dim)" style={{ display: "block", margin: "0 auto 12px", opacity: 0.5 }} />
                    <h4 style={{ fontFamily: "DM Sans", fontSize: "16px", fontWeight: 600, color: "var(--text-main)", marginBottom: "4px" }}>
                      No matching policies
                    </h4>
                    <p style={{ fontFamily: "DM Sans", fontSize: "14px", color: "var(--text-muted)", margin: 0 }}>
                      Try adjusting your search query or choosing another sector filter.
                    </p>
                  </div>
                ) : (
                  <div style={{ 
                    display: "grid", 
                    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", 
                    gap: "16px", 
                    maxHeight: "560px", 
                    overflowY: "auto", 
                    paddingRight: "6px" 
                  }}>
                    {filtered.map(p => {
                      const isHovered = hoveredPolicy === p.id;
                      return (
                        <div 
                          key={p.id}
                          onClick={() => handleSelect(p)}
                          onMouseEnter={() => setHoveredPolicy(p.id)}
                          onMouseLeave={() => setHoveredPolicy(null)}
                          style={{
                            background: "var(--bg-card)",
                            border: isHovered ? "1px solid #5c9e2e" : "1px solid var(--border)",
                            borderRadius: "8px",
                            padding: "16px",
                            cursor: "pointer",
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            boxShadow: isHovered ? "0 4px 12px rgba(92, 158, 46, 0.06)" : "none",
                            transform: isHovered ? "translateY(-2px)" : "translateY(0)"
                          }}
                        >
                          <div>
                            {/* Card Header: Sector & External Link */}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                              <span style={{ 
                                fontSize: "11px", 
                                fontFamily: "JetBrains Mono", 
                                color: "var(--text-muted)",
                                background: "var(--bg-hover)",
                                border: "1px solid var(--border)",
                                borderRadius: "4px",
                                padding: "2px 6px",
                                textTransform: "uppercase"
                              }}>
                                {p.sector}
                              </span>
                              
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  nav(`/policies/${p.id}`);
                                }}
                                title="View source database page"
                                style={{
                                  background: "none",
                                  border: "none",
                                  padding: "4px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  color: isHovered ? "#5c9e2e" : "var(--text-dim)",
                                  borderRadius: "4px",
                                  transition: "all 0.15s ease",
                                  outline: "none"
                                }}
                              >
                                <ExternalLink size={13} />
                              </button>
                            </div>

                            {/* Card Title */}
                            <h4 style={{
                              fontFamily: "DM Sans",
                              fontWeight: 600,
                              fontSize: "14px",
                              color: "var(--text-main)",
                              margin: "0 0 16px 0",
                              lineHeight: "1.45",
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis"
                            }}>
                              {p.title}
                            </h4>
                          </div>

                          {/* Card Footer */}
                          <div style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            justifyContent: "space-between",
                            borderTop: "1px solid var(--border-lit)",
                            paddingTop: "10px",
                            marginTop: "8px"
                          }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <span style={{ fontSize: "11px", fontFamily: "JetBrains Mono", color: "var(--text-muted)", textTransform: "uppercase" }}>
                                {p.country}
                              </span>
                              {p.year && (
                                <>
                                  <span style={{ fontSize: "11px", fontFamily: "JetBrains Mono", color: "var(--text-dim)" }}>·</span>
                                  <span style={{ fontSize: "11px", fontFamily: "JetBrains Mono", color: "var(--text-muted)" }}>
                                    {p.year}
                                  </span>
                                </>
                              )}
                            </div>
                            
                            {/* Action hint */}
                            <span style={{
                              fontSize: "10px",
                              fontFamily: "JetBrains Mono",
                              color: isHovered ? "#5c9e2e" : "transparent",
                              transition: "color 0.15s ease",
                              fontWeight: 600
                            }}>
                              SELECT →
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ========================================================================= */}
        {/* MODE B: COLLAPSED SELECTION & SINGLE COLUMN RESULTS (step === 2) */}
        {/* ========================================================================= */}
        {step === 2 && (
          <div style={{ width: "100%" }}>
            
            {/* Top Navigation Row */}
            <div style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center", 
              marginBottom: "20px" 
            }}>
              <div>
                {/* Breadcrumbs */}
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                  <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "var(--cyan)" }} />
                  <span style={{ fontSize: "12px", fontFamily: "JetBrains Mono", color: "var(--text-muted)", letterSpacing: "0.1em" }}>
                    DISCOVER / INTELLIGENCE
                  </span>
                </div>
                <div 
                  onClick={() => {
                    setStep(1);
                    setResult(null);
                    setSelected(null);
                    setSearch("");
                  }}
                  onMouseEnter={() => setHoveredBack(true)}
                  onMouseLeave={() => setHoveredBack(false)}
                  style={{
                    fontSize: "13px",
                    fontFamily: "JetBrains Mono",
                    color: hoveredBack ? "var(--cyan)" : "var(--text-muted)",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    transition: "color 0.15s ease",
                    marginTop: "4px"
                  }}
                >
                  ← Change policy
                </div>
              </div>
              
              {result && !loading && (
                <div className="flex-toolbar" style={{ gap: "16px" }}>
                  <div style={{ fontSize: "12px", fontFamily: "JetBrains Mono", color: "var(--text-muted)" }}>
                    {result.recommendations?.length || 0} recommendations
                  </div>
                  <button
                    onClick={handleDownloadRecsPDF}
                    style={{
                      background: "rgba(92, 158, 46, 0.1)",
                      border: "1px solid var(--accent)",
                      borderRadius: "6px",
                      color: "var(--accent)",
                      fontFamily: "JetBrains Mono",
                      fontSize: "12px",
                      fontWeight: 600,
                      padding: "6px 12px",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      transition: "all 0.15s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "var(--accent)";
                      e.currentTarget.style.color = "var(--bg-card)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(92, 158, 46, 0.1)";
                      e.currentTarget.style.color = "var(--accent)";
                    }}
                  >
                    <FileDown size={14} />
                    Download PDF Report
                  </button>
                </div>
              )}
            </div>

            {/* Selected Policy Compact Card */}
            {selected && (
              <div 
                className="selected-policy-compact-card-custom"
                style={{
                  marginBottom: "16px"
                }}
              >
                {/* Left avatar */}
                <div style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "6px",
                  background: "var(--bg-card)",
                  border: "1px solid var(--border)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0
                }}>
                  <span style={{ fontSize: "16px", fontFamily: "DM Sans", color: "var(--text-muted)", fontWeight: 600 }}>
                    {selected.title ? selected.title.charAt(0).toUpperCase() : "P"}
                  </span>
                </div>

                {/* Center */}
                <div className="card-center" style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "11px", fontFamily: "JetBrains Mono", color: "var(--text-muted)", textTransform: "uppercase" }}>
                    ANALYZING
                  </div>
                  <h4 
                    onClick={() => nav(`/policies/${selected.id}`)}
                    title="View source database page"
                    className="selected-policy-title-hover"
                    style={{ 
                      fontFamily: "DM Sans", 
                      fontSize: "16px", 
                      fontWeight: 600, 
                      color: "var(--text-main)", 
                      margin: "2px 0 6px 0",
                      cursor: "pointer",
                      lineHeight: "1.4",
                      overflowWrap: "anywhere"
                    }}
                  >
                    {selected.title}
                    <ExternalLink size={13} style={{ color: "var(--text-muted)", marginLeft: "6px", display: "inline-block", verticalAlign: "middle" }} />
                  </h4>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <span style={{
                      fontSize: "11px",
                      fontFamily: "JetBrains Mono",
                      color: "var(--cyan)",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px"
                    }}>
                      <span style={{ width: "4px", height: "4px", borderRadius: "50%", background: "var(--cyan)" }} />
                      {selected.sector}
                    </span>
                    <span style={{ color: "var(--border)" }}>·</span>
                    <span style={{ fontSize: "12px", fontFamily: "JetBrains Mono", color: "var(--text-muted)", textTransform: "uppercase" }}>
                      {selected.country}
                    </span>
                    {selected.cluster && (
                      <>
                        <span style={{ color: "var(--border)" }}>·</span>
                        <span style={{ fontSize: "12px", fontFamily: "JetBrains Mono", color: "var(--text-muted)" }}>
                          CLUSTER #{selected.cluster}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Right */}
                <div style={{
                  fontSize: "11px",
                  fontFamily: "JetBrains Mono",
                  color: "var(--text-muted)",
                  border: "1px solid var(--border)",
                  borderRadius: "4px",
                  padding: "4px 10px",
                  background: "var(--bg-card)",
                  flexShrink: 0
                }}>
                  Intellect AI · 5-Factor Gap Engine
                </div>
              </div>
            )}

            {/* Verified Source Banner */}
            {selected && (
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 16px",
                background: "var(--bg-hover)",
                border: "1px solid var(--border)",
                borderRadius: "6px",
                marginBottom: "12px"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Shield size={14} color="var(--cyan)" />
                  <span style={{ fontSize: "11px", fontFamily: "JetBrains Mono", color: "var(--text-muted)" }}>
                    VERIFIED SOURCE
                  </span>
                  <span style={{ fontSize: "11px", fontFamily: "JetBrains Mono", color: "var(--text-muted)" }}>
                    {result?.source_policy?.integrity_hash 
                      ? (result.source_policy.integrity_hash.slice(0, 20) + "...") 
                      : selected.integrity_hash 
                        ? (selected.integrity_hash.slice(0, 20) + "...") 
                        : "sha256-..."}
                  </span>
                  <Copy 
                    size={11} 
                    color="var(--border-lit)" 
                    style={{ cursor: "pointer" }} 
                    onClick={() => {
                      const hash = result?.source_policy?.integrity_hash || selected.integrity_hash || "sha256-dummy-hash-value-for-testing";
                      navigator.clipboard.writeText(hash);
                      alert("Integrity hash copied to clipboard!");
                    }}
                  />
                </div>
                {(result?.source_policy?.source_url || selected.source_url) && (
                  <a 
                    href={result?.source_policy?.source_url || selected.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: "11px", 
                      fontFamily: "JetBrains Mono", 
                      color: "var(--cyan)",
                      textDecoration: "none",
                      cursor: "pointer",
                      fontWeight: 600
                    }}
                  >
                    SOURCE ↗
                  </a>
                )}
              </div>
            )}

            {/* Loading Recommendations State — Stepped Progress */}
            {loading && (
              <div style={{
                background: "var(--bg-card)",
                border: "1px solid var(--border)",
                borderRadius: "8px",
                padding: "40px 48px",
                marginTop: "16px",
                width: "100%"
              }}>
                {/* Header row */}
                <div className="flex-toolbar" style={{ justifyContent: "space-between", marginBottom: "24px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "var(--cyan)", animation: "spin 1.5s linear infinite" }} />
                    <span style={{ fontSize: "11px", fontFamily: "JetBrains Mono", color: "var(--text-muted)", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                      ML Pipeline Active
                    </span>
                  </div>
                  <span style={{ fontSize: "11px", fontFamily: "JetBrains Mono", color: "var(--text-dim)" }}>
                    Step {recLoadingStep + 1} of {RECOMMEND_LOADING_STEPS.length}
                  </span>
                </div>

                {/* Progress bar */}
                <div style={{ height: "4px", background: "var(--bg-hover)", borderRadius: "2px", marginBottom: "20px", overflow: "hidden" }}>
                  <div style={{
                    height: "100%",
                    width: `${((recLoadingStep + 1) / RECOMMEND_LOADING_STEPS.length) * 100}%`,
                    background: "var(--cyan)",
                    borderRadius: "2px",
                    transition: "width 0.8s ease"
                  }} />
                </div>

                {/* Current step message */}
                <h3 style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "var(--text-main)",
                  marginBottom: "8px",
                  textAlign: "center"
                }}>
                  {RECOMMEND_LOADING_STEPS[recLoadingStep]}
                </h3>
                <p style={{
                  fontFamily: "JetBrains Mono",
                  fontSize: "12px",
                  color: "var(--text-dim)",
                  textAlign: "center",
                  marginBottom: "20px"
                }}>
                  Analyzing "{selected?.title?.slice(0, 50)}{selected?.title?.length > 50 ? '...' : ''}" · ~15-20s
                </p>

                {/* Step list */}
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxWidth: "400px", margin: "0 auto" }}>
                  {RECOMMEND_LOADING_STEPS.map((step, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", opacity: i <= recLoadingStep ? 1 : 0.35, transition: "opacity 0.4s ease" }}>
                      <div style={{
                        width: "20px", height: "20px", borderRadius: "50%",
                        background: i < recLoadingStep ? "var(--cyan)" : i === recLoadingStep ? "var(--bg-hover)" : "var(--bg-hover)",
                        border: i === recLoadingStep ? "2px solid var(--cyan)" : i < recLoadingStep ? "none" : "1px solid var(--border)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "10px", fontWeight: 700, color: i < recLoadingStep ? "#000" : "var(--text-dim)",
                        flexShrink: 0, transition: "all 0.3s ease"
                      }}>
                        {i < recLoadingStep ? "✓" : i + 1}
                      </div>
                      <span style={{
                        fontSize: "13px",
                        fontFamily: "DM Sans",
                        color: i === recLoadingStep ? "var(--text-main)" : "var(--text-muted)",
                        fontWeight: i === recLoadingStep ? 600 : 400
                      }}>
                        {step.replace('...', '')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Results Block */}
            {result && !loading && (
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

                {/* Scoring Legend Card */}
                <div style={{
                  background: "var(--bg-hover)",
                  border: "1px solid var(--border)",
                  borderRadius: "8px",
                  padding: "16px 20px",
                  marginBottom: "8px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Target size={14} color="var(--accent)" />
                    <span style={{ fontSize: "12px", fontFamily: "JetBrains Mono", color: "var(--text-main)", fontWeight: 600 }}>
                      NEED INDEX METHODOLOGY & LEGEND
                    </span>
                  </div>
                  <p style={{ fontSize: "13px", color: "var(--text-muted)", margin: 0, lineHeight: "1.5" }}>
                    The Need Index is a normalized metric from <code style={{ fontFamily: "JetBrains Mono", color: "var(--cyan)" }}>0.000</code> to <code style={{ fontFamily: "JetBrains Mono", color: "var(--cyan)" }}>1.000</code> calculated using a multi-factor regulatory alignment heuristic. To avoid arbitrary thresholds, the system computes the score dynamically using the following weights:
                  </p>
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                    gap: "10px",
                    marginTop: "8px"
                  }}>
                    {[
                      { name: "Sector Gap Severity", w: weights.sector_gap, desc: "Absence of active legislation in primary sector." },
                      { name: "Statutory Infrastructure", w: weights.regulatory_maturity, desc: "Country's baseline regulatory maturity tier." },
                      { name: "Legislative Intent", w: weights.semantic_need, desc: "Semantic alignment using Legal-BERT embeddings." },
                      { name: "Geopolitical Peer Pressure", w: weights.regional_pressure, desc: "Adoption velocity in adjacent regional markets." },
                      { name: "Developmental Tier", w: weights.economic_tier, desc: "Socioeconomic and GDP tier alignment." }
                    ].map((f, i) => (
                      <div key={i} style={{
                        background: "var(--bg-card)",
                        border: "1px solid var(--border)",
                        borderRadius: "6px",
                        padding: "8px 12px",
                        fontSize: "12px"
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 600, color: "var(--text-main)", marginBottom: "4px" }}>
                          <span>{f.name}</span>
                          <span style={{ color: "var(--cyan)" }}>{f.w}%</span>
                        </div>
                        <span style={{ color: "var(--text-muted)", fontSize: "11px", display: "block", lineHeight: "1.3" }}>{f.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Factor Weights Panel */}
                <div style={{
                  background: "var(--bg-card)",
                  border: "1px solid var(--border)",
                  borderRadius: "8px",
                  padding: "20px 24px",
                  marginBottom: "20px"
                }}>
                  {/* Header Row */}
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: showWeightsPanel ? "16px" : "4px",
                    flexWrap: "wrap",
                    gap: "12px"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, marginRight: "16px" }}>
                      <div style={{ width: "20px", height: "1px", background: "var(--border)" }} />
                      <span style={{ fontSize: "12px", fontFamily: "JetBrains Mono", color: "var(--text-muted)", letterSpacing: "0.12em" }}>
                        FACTOR WEIGHTS
                      </span>
                      <div style={{ flex: 1, height: "1px", background: "var(--border)" }} />
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}>
                      <span style={{
                        fontSize: "12px",
                        fontFamily: "JetBrains Mono",
                        color: weightsSum === 100 ? "var(--cyan)" : "var(--red)",
                        fontWeight: 600
                      }}>
                        {weightsSum}%
                      </span>
                      <span 
                        onClick={() => setShowWeightsPanel(!showWeightsPanel)}
                        style={{
                          fontSize: "12px",
                          fontFamily: "JetBrains Mono",
                          color: "var(--text-muted)",
                          cursor: "pointer",
                          marginLeft: "12px"
                        }}
                        onMouseEnter={(e) => e.target.style.color = "var(--text-main)"}
                        onMouseLeave={(e) => e.target.style.color = "var(--text-muted)"}
                      >
                        {showWeightsPanel ? "hide" : "show"}
                      </span>
                    </div>
                  </div>

                  {showWeightsPanel && (
                    <>
                      <p style={{
                        fontSize: "14px",
                        fontFamily: "DM Sans",
                        color: "var(--text-muted)",
                        marginBottom: "16px",
                        marginTop: 0
                      }}>
                        Adjust factor significance. Recalculates recommendations in real-time.
                      </p>

                      <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                        gap: "16px 24px"
                      }}>
                        {[
                          { key: "sector_gap", label: "Regulatory Gap Severity", desc: "35" },
                          { key: "regulatory_maturity", label: "Statutory Infrastructure", desc: "25" },
                          { key: "semantic_need", label: "Legislative Intent Match", desc: "20" },
                          { key: "regional_pressure", label: "Geopolitical Peer Adoption", desc: "12" },
                          { key: "economic_tier", label: "Developmental Alignment", desc: "8" },
                        ].map((item) => (
                          <div key={item.key} style={{ display: "flex", flexDirection: "column" }}>
                            <div style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginBottom: "6px",
                              alignItems: "flex-end",
                              flexWrap: "wrap",
                              gap: "4px 8px"
                            }}>
                              <span style={{ fontSize: "14px", fontFamily: "DM Sans", fontWeight: 500, color: "var(--text-main)" }}>
                                {item.label}
                              </span>
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", textAlign: "right" }}>
                                <span style={{ fontSize: "14px", fontFamily: "JetBrains Mono", color: "var(--text-main)", fontWeight: 600 }}>
                                  {weights[item.key]}%
                                </span>
                                <span style={{ fontSize: "10px", fontFamily: "JetBrains Mono", color: "var(--text-muted)", marginTop: "2px" }}>
                                  (default {item.desc}%)
                                </span>
                              </div>
                            </div>

                            <input 
                              type="range"
                              min="0"
                              max="100"
                              value={weights[item.key]}
                              onChange={(e) => handleWeightChange(item.key, parseInt(e.target.value))}
                              style={{
                                width: "100%",
                                height: "4px",
                                background: "var(--border)",
                                borderRadius: "2px",
                                outline: "none",
                                appearance: "none",
                                cursor: "pointer",
                                accentColor: "var(--text-main)"
                              }}
                              className="custom-slider-input"
                            />
                          </div>
                        ))}
                      </div>

                      {/* Action buttons row */}
                      <div className="weights-actions-row">
                        <button 
                          onClick={handleResetWeights}
                          style={{
                            border: "1px solid var(--border)",
                            background: "var(--bg-card)",
                            color: "var(--text-muted)",
                            fontSize: "14px",
                            fontFamily: "DM Sans",
                            padding: "12px 24px",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontWeight: 500,
                            transition: "all 0.15s ease"
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.borderColor = "var(--border-lit)";
                            e.target.style.color = "var(--text-main)";
                            e.target.style.background = "var(--bg-hover)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.borderColor = "var(--border)";
                            e.target.style.color = "var(--text-muted)";
                            e.target.style.background = "var(--bg-card)";
                          }}
                        >
                          Reset to defaults
                        </button>
                        <button 
                          onClick={handleAutoBalance}
                          style={{
                            border: "1px solid var(--border)",
                            background: "var(--bg-card)",
                            color: "var(--text-muted)",
                            fontSize: "14px",
                            fontFamily: "DM Sans",
                            padding: "12px 24px",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontWeight: 500,
                            transition: "all 0.15s ease"
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.borderColor = "var(--border-lit)";
                            e.target.style.color = "var(--text-main)";
                            e.target.style.background = "var(--bg-hover)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.borderColor = "var(--border)";
                            e.target.style.color = "var(--text-muted)";
                            e.target.style.background = "var(--bg-card)";
                          }}
                        >
                          Auto-Balance
                        </button>
                        <button 
                          onClick={handleRecalculate}
                          disabled={loading}
                          style={{
                            background: "var(--text-main)",
                            color: "var(--bg-card)",
                            fontSize: "14px",
                            fontFamily: "DM Sans",
                            fontWeight: 600,
                            padding: "12px 28px",
                            borderRadius: "6px",
                            cursor: "pointer",
                            border: "none",
                            transition: "all 0.15s ease"
                          }}
                          onMouseEnter={(e) => { e.target.style.background = "var(--text-muted)"; }}
                          onMouseLeave={(e) => { e.target.style.background = "var(--text-main)"; }}
                        >
                          {loading ? "Recalculating..." : "Recalculate"}
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Similar Policies Section */}
                {result?.similar_policies?.length > 0 && (
                  <div style={{
                    background: "var(--bg-card)",
                    border: "1px solid var(--border)",
                    borderRadius: "8px",
                    padding: "18px 22px",
                    marginBottom: "20px"
                  }}>
                    {/* Section label */}
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
                      <div style={{ width: "20px", height: "1px", background: "var(--border)" }} />
                      <span style={{ fontSize: "12px", fontFamily: "JetBrains Mono", color: "var(--text-muted)", letterSpacing: "0.12em" }}>
                        THEMATICALLY SIMILAR POLICIES
                      </span>
                      <div style={{ flex: 1, height: "1px", background: "var(--border)" }} />
                    </div>
                    
                    <div style={{
                      fontSize: "13px",
                      fontFamily: "DM Sans",
                      color: "var(--text-muted)",
                      marginBottom: "14px"
                    }}>
                      Regulatory Similarity · Underpinning baseline comparative context
                    </div>

                    {/* Horizontal scroll list */}
                    <div style={{
                      display: "flex",
                      gap: "10px",
                      overflowX: "auto",
                      paddingBottom: "8px"
                    }}>
                      {result.similar_policies.map((p, idx) => (
                        <div 
                          key={idx} 
                          onClick={() => nav(`/policies/${p.id}`)}
                          className="similar-policy-card-hover"
                          title={`Click to view ${p.title} details`}
                          style={{
                            background: "var(--bg-hover)",
                            border: "1px solid var(--border)",
                            borderRadius: "6px",
                            padding: "10px 14px",
                            flexShrink: 0,
                            minWidth: "200px",
                            maxWidth: "240px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            position: "relative"
                          }}>
                          <div>
                            <div style={{
                              fontFamily: "DM Sans",
                              fontWeight: 500,
                              fontSize: "13px",
                              color: "var(--text-main)",
                              lineHeight: "1.4",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              paddingRight: "36px"
                            }}>
                              {p.title}
                            </div>
                            <div style={{ fontSize: "11px", fontFamily: "JetBrains Mono", color: "var(--text-muted)", marginTop: "4px" }}>
                              {p.country} · {p.sector}
                            </div>
                          </div>
                          <div style={{
                            position: "absolute",
                            top: "10px",
                            right: "14px",
                            fontFamily: "JetBrains Mono",
                            fontSize: "12px",
                            color: "var(--cyan)",
                            fontWeight: 600
                          }}>
                            {Math.round((p.approx_similarity ?? p.similarity ?? 0) * 100)}%
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommendations Cards Stack */}
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  {result.recommendations.map((rec, i) => {
                    const needPercentVal = rec.need_score * 100;
                    const colorAccent = needPercentVal > 85 
                      ? "#5c9e2e" 
                      : needPercentVal > 70 
                        ? "#d97706" 
                        : "#2563eb";

                    const isReasonExpanded = expandedReasons[rec.country];
                    const needsReasonTruncation = rec.reasoning && rec.reasoning.length > 200;
                    const reasoningDisplayText = (needsReasonTruncation && !isReasonExpanded)
                      ? rec.reasoning.slice(0, 180) + "..."
                      : rec.reasoning;

                    return (
                      <div key={rec.country} style={{
                        background: "white",
                        border: "1px solid #e8e8e8",
                        borderRadius: "8px",
                        marginBottom: "16px",
                        overflow: "hidden"
                      }}>
                        
                        {/* TOP ACCENT BAR */}
                        <div style={{
                          height: "3px",
                          width: "100%",
                          background: colorAccent
                        }} />

                        {/* CARD CONTENT */}
                        <div style={{ padding: "20px 24px" }}>
                          
                          {/* TOP ROW (country header) */}
                          <div className="rec-card-header">
                            <div className="rec-header-left">
                              <div className="rec-title-row">
                                <span style={{ fontFamily: "JetBrains Mono", fontSize: "12px", color: "#9ca3af" }}>
                                  #{i + 1}
                                </span>
                                <span style={{ fontFamily: "DM Sans", fontWeight: 700, fontSize: "22px", color: "var(--text-main)" }}>
                                  {rec.country}
                                </span>
                                <span style={{ fontFamily: "DM Sans", fontSize: "14px", color: "#9ca3af", whiteSpace: "nowrap" }}>
                                  · {rec.region}
                                </span>
                              </div>

                              <div className="rec-badges-row">
                                {rec.already_has_sector ? (
                                  <span className="badge-custom badge-custom--neutral">
                                    has sector
                                  </span>
                                ) : (
                                  <span className="badge-custom badge-custom--danger">
                                    Missing sector
                                  </span>
                                )}

                                <span className={`badge-custom badge-custom--${rec.regulatory_maturity?.toLowerCase() || 'developing'}`}>
                                  {rec.regulatory_maturity?.toUpperCase()} MATURITY
                                </span>
                              </div>
                            </div>

                            <div className="rec-header-right">
                              <div style={{ fontSize: "11px", fontFamily: "JetBrains Mono", color: "#9ca3af", letterSpacing: "0.08em" }}>
                                NEED INDEX
                              </div>
                              <div style={{
                                fontFamily: "DM Sans",
                                fontSize: "28px",
                                fontWeight: 700,
                                color: colorAccent,
                                lineHeight: "1"
                              }}>
                                {rec.need_score.toFixed(3)}
                              </div>
                            </div>
                          </div>

                          {/* SCORE BAR */}
                          <div style={{
                            height: "3px",
                            width: "100%",
                            background: "#f0f0f0",
                            borderRadius: "2px",
                            marginBottom: "20px"
                          }}>
                            <div style={{
                              height: "100%",
                              borderRadius: "2px",
                              width: `${rec.need_score * 100}%`,
                              background: colorAccent,
                              transition: "width 0.8s ease"
                            }} />
                          </div>

                          {/* THREE-COLUMN CONTENT GRID */}
                          <div 
                            className="recommender-details-grid-custom"
                            style={{
                              gap: "20px"
                            }}
                          >
                            
                            {/* COLUMN 1 — "WHY THIS COUNTRY" */}
                            <div>
                              <div style={{
                                fontSize: "11px",
                                fontFamily: "JetBrains Mono",
                                color: "#9ca3af",
                                letterSpacing: "0.1em",
                                marginBottom: "8px"
                              }}>
                                WHY THIS COUNTRY
                              </div>
                              <div style={{
                                background: "#fafafa",
                                border: "1px solid #e8e8e8",
                                borderRadius: "6px",
                                padding: "14px 16px"
                              }}>
                                <p style={{
                                  fontSize: "14px",
                                  fontFamily: "DM Sans",
                                  color: "#374151",
                                  lineHeight: "1.7",
                                  margin: 0
                                }}>
                                  {reasoningDisplayText}
                                </p>
                                {needsReasonTruncation && (
                                  <button
                                    onClick={() => toggleReason(rec.country)}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      color: "#5c9e2e",
                                      fontSize: "12px",
                                      fontFamily: "JetBrains Mono",
                                      cursor: "pointer",
                                      padding: 0,
                                      marginTop: "8px",
                                      fontWeight: 600
                                    }}
                                  >
                                    {isReasonExpanded ? "SHOW LESS" : "READ FULL REASONING"}
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* COLUMN 2 — "EXPECTED BENEFITS" */}
                            <div>
                              <div style={{
                                fontSize: "11px",
                                fontFamily: "JetBrains Mono",
                                color: "#9ca3af",
                                letterSpacing: "0.1em",
                                marginBottom: "8px"
                              }}>
                                EXPECTED BENEFITS
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                {rec.expected_benefits.map((b, idx) => (
                                  <div key={idx} style={{ display: "flex", gap: "8px", alignItems: "flex-start", marginBottom: "8px" }}>
                                    <span style={{ color: "#5c9e2e", fontWeight: 600, flexShrink: 0 }}>—</span>
                                    <span style={{ fontSize: "14px", fontFamily: "DM Sans", color: "#374151", lineHeight: "1.6" }}>
                                      {b}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* COLUMN 3 — RADAR CHART */}
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                              <div style={{ width: "200px", height: "180px" }}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <RadarChart cx="50%" cy="50%" outerRadius="55%" data={[
                                    { subject: "Gap Severity", score: Math.round((rec.score_breakdown.sector_gap / (weights.sector_gap / 100 || 1)) * 100) },
                                    { subject: "Infrastructure", score: Math.round((rec.score_breakdown.regulatory_maturity / (weights.regulatory_maturity / 100 || 1)) * 100) },
                                    { subject: "Intent Match", score: Math.round((rec.score_breakdown.semantic_need / (weights.semantic_need / 100 || 1)) * 100) },
                                    { subject: "Peer Adoption", score: Math.round((rec.score_breakdown.regional_pressure / (weights.regional_pressure / 100 || 1)) * 100) },
                                    { subject: "Development", score: Math.round((rec.score_breakdown.economic_tier / (weights.economic_tier / 100 || 1)) * 100) }
                                  ]}>
                                    <PolarGrid stroke="#e8e8e8" />
                                    <PolarAngleAxis dataKey="subject" tick={{ fill: "#9ca3af", fontSize: 10, fontFamily: "JetBrains Mono" }} />
                                    <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                                    <Radar 
                                      name="Need Index" 
                                      dataKey="score" 
                                      stroke="#5c9e2e" 
                                      fill="#5c9e2e" 
                                      fillOpacity={0.08} 
                                      strokeWidth={1.5}
                                    />
                                  </RadarChart>
                                </ResponsiveContainer>
                              </div>

                              {/* Factor values mini grid */}
                              <div className="score-grid" style={{
                                textAlign: "center",
                                marginTop: "8px",
                                paddingTop: "8px",
                                borderTop: "1px solid #e8e8e8",
                                width: "100%"
                              }}>
                                {[
                                  { label: "Gap Severity", val: rec.score_breakdown.sector_gap },
                                  { label: "Infrastructure", val: rec.score_breakdown.regulatory_maturity },
                                  { label: "Intent Match", val: rec.score_breakdown.semantic_need },
                                  { label: "Peer Adoption", val: rec.score_breakdown.regional_pressure },
                                  { label: "Development", val: rec.score_breakdown.economic_tier }
                                ].map(itm => (
                                  <div key={itm.label} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                    <span style={{ fontSize: "11px", color: "#9ca3af", fontFamily: "JetBrains Mono" }}>
                                      {itm.label.slice(0, 3)}
                                    </span>
                                    <span style={{ fontSize: "12px", fontFamily: "JetBrains Mono", color: "#0a0a0a", fontWeight: 600 }}>
                                      {itm.val.toFixed(1)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>

                          </div>

                          {/* BOTTOM ROW (card footer) */}
                          <div className="recommender-footer-row-custom">
                            {/* AI Proposal button */}
                            <button
                              onClick={() => handleGenerateProposal(rec.country, selected.sector)}
                              onMouseEnter={() => setHoveredProposalBtn(prev => ({ ...prev, [rec.country]: true }))}
                              onMouseLeave={() => setHoveredProposalBtn(prev => ({ ...prev, [rec.country]: false }))}
                              style={{
                                background: hoveredProposalBtn[rec.country] ? "#f0f7e8" : "white",
                                border: hoveredProposalBtn[rec.country] ? "1px solid #5c9e2e" : "1px solid #e8e8e8",
                                color: hoveredProposalBtn[rec.country] ? "#3d6b1e" : "#374151",
                                borderRadius: "6px",
                                padding: "8px 14px",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                cursor: "pointer",
                                transition: "all 0.15s ease",
                                outline: "none"
                              }}
                            >
                              <Sparkles size={13} color="#5c9e2e" />
                              <span style={{ fontSize: "13px", fontFamily: "DM Sans", fontWeight: 500 }}>
                                Generate AI Proposal
                              </span>
                            </button>

                            {/* Feedback buttons */}
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }} className="feedback-container">
                              <span style={{ fontSize: "12px", fontFamily: "DM Sans", color: "#9ca3af", marginRight: "8px" }}>
                                Was this relevant?
                              </span>
                              <div className="feedback-buttons-wrapper">
                                <button 
                                  onClick={() => handleFeedback(rec.country, true)}
                                  onMouseEnter={() => setHoveredFeedbackBtn(prev => ({ ...prev, [`${rec.country}_pos`]: true }))}
                                  onMouseLeave={() => setHoveredFeedbackBtn(prev => ({ ...prev, [`${rec.country}_pos`]: false }))}
                                  style={{
                                    border: feedbackGiven[rec.country] === "positive" ? "1px solid #5c9e2e" : "1px solid #e8e8e8",
                                    background: feedbackGiven[rec.country] === "positive" 
                                      ? "#f0f7e8" 
                                      : hoveredFeedbackBtn[`${rec.country}_pos`] 
                                        ? "#fafafa" 
                                        : "white",
                                    color: feedbackGiven[rec.country] === "positive" ? "#3d6b1e" : "#6b7280",
                                    fontSize: "12px",
                                    fontFamily: "DM Sans",
                                    padding: "6px 12px",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    transition: "all 0.15s ease"
                                  }}
                                >
                                  <ThumbsUp size={12} style={{ marginRight: "4px" }} />
                                  Helpful
                                </button>
                                <button 
                                  onClick={() => handleFeedback(rec.country, false)}
                                  onMouseEnter={() => setHoveredFeedbackBtn(prev => ({ ...prev, [`${rec.country}_neg`]: true }))}
                                  onMouseLeave={() => setHoveredFeedbackBtn(prev => ({ ...prev, [`${rec.country}_neg`]: false }))}
                                  style={{
                                    border: feedbackGiven[rec.country] === "negative" ? "1px solid #fecaca" : "1px solid #e8e8e8",
                                    background: feedbackGiven[rec.country] === "negative" 
                                      ? "#fef2f2" 
                                      : hoveredFeedbackBtn[`${rec.country}_neg`] 
                                        ? "#fafafa" 
                                        : "white",
                                    color: feedbackGiven[rec.country] === "negative" ? "#dc2626" : "#6b7280",
                                    fontSize: "12px",
                                    fontFamily: "DM Sans",
                                    padding: "6px 12px",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    transition: "all 0.15s ease"
                                  }}
                                >
                                  <ThumbsDown size={12} style={{ marginRight: "4px" }} />
                                  Not Relevant
                                </button>
                              </div>
                            </div>
                          </div>

                        </div>
                      </div>
                    );
                  })}
                </div>

              </div>
            )}

          </div>
        )}

      </div>

      {/* ========================================================================= */}
      {/* SLIDING AI BRIEF DRAWER */}
      {/* ========================================================================= */}
      {drawerOpen && (
        <div style={{
          position: "fixed",
          top: 0, right: 0, bottom: 0,
          width: "100%",
          maxWidth: "540px",
          background: "white",
          borderLeft: "1px solid #e8e8e8",
          boxShadow: "-8px 0 24px rgba(0,0,0,0.06)",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          color: "#0a0a0a",
          animation: "slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards"
        }}>
          
          {/* Drawer Header */}
          <div style={{
            padding: "24px 28px",
            borderBottom: "1px solid #e8e8e8",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                <Sparkles size={14} color="#5c9e2e" />
                <span style={{ fontSize: "12px", fontFamily: "JetBrains Mono", color: "#5c9e2e", letterSpacing: "0.1em" }}>
                  AI LEGISLATIVE SYNTHESIS
                </span>
              </div>
              <h2 style={{ fontFamily: "DM Sans", fontSize: "22px", fontWeight: 700, margin: 0 }}>
                Executive Brief & Draft Proposal
              </h2>
            </div>
            <button 
              onClick={() => setDrawerOpen(false)}
              style={{
                background: "transparent",
                border: "none",
                color: "#9ca3af",
                fontSize: "20px",
                cursor: "pointer",
                padding: "4px"
              }}
            >
              ✕
            </button>
          </div>

          {/* Drawer Body */}
          <div style={{ flex: 1, overflowY: "auto", padding: "28px" }}>
            {drawerLoading && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: "16px" }}>
                <LoadingSpinner label="Drafting professional policy framework..." />
                <p style={{ color: "#9ca3af", fontSize: "14px", textAlign: "center", maxWidth: "80%", fontFamily: "DM Sans" }}>
                  Running semantic gap synthesis, mapping developmental parameters, and assembling implementation timelines.
                </p>
              </div>
            )}

            {proposal && !drawerLoading && (
              <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                
                {/* Proposal Title */}
                <div style={{ 
                  padding: "16px 20px", 
                  background: "#fafafa", 
                  border: "1px solid #e8e8e8",
                  borderRadius: "8px"
                }}>
                  <div style={{ fontSize: "12px", color: "#5c9e2e", fontFamily: "JetBrains Mono", marginBottom: "6px" }}>
                    DRAFT STATUTORY BILL TITLE
                  </div>
                  <h3 style={{ fontFamily: "DM Sans", fontSize: "18px", fontWeight: 700, margin: 0, color: "#0a0a0a" }}>
                    {proposal.suggested_title}
                  </h3>
                </div>

                {/* Country Context */}
                <div>
                  <div style={{ fontSize: "12px", color: "#9ca3af", fontFamily: "JetBrains Mono", marginBottom: "6px" }}>
                    NATIONAL DEVELOPMENT CONTEXT & STATUTORY JUSTIFICATION
                  </div>
                  <div style={{ background: "#ffffff", padding: "14px 18px", borderRadius: "8px", border: "1px solid #e8e8e8" }}>
                    <p style={{ fontSize: "14px", lineHeight: "1.6", color: "#374151", margin: 0, fontFamily: "DM Sans" }}>
                      {proposal.policy_context}
                    </p>
                  </div>
                </div>

                {/* Focus Areas */}
                <div>
                  <div style={{ fontSize: "12px", color: "#9ca3af", fontFamily: "JetBrains Mono", marginBottom: "6px" }}>
                    PRIORITY STRATEGIC DEVELOPMENT AREAS
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    {proposal.priority_areas?.map((area, idx) => (
                      <div key={idx} style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: "8px", 
                        background: "#fafafa", 
                        padding: "8px 12px", 
                        borderRadius: "6px", 
                        border: "1px solid #e8e8e8" 
                      }}>
                        <span style={{ color: "#5c9e2e", fontSize: "13px", fontFamily: "JetBrains Mono", fontWeight: 600 }}>0{idx + 1}</span>
                        <span style={{ fontSize: "14px", color: "#374151", fontFamily: "DM Sans" }}>{area}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recommended Bill Sections */}
                <div>
                  <div style={{ fontSize: "12px", color: "#9ca3af", fontFamily: "JetBrains Mono", marginBottom: "6px" }}>
                    RECOMMENDED BILL SECTIONS
                  </div>
                  <div style={{ background: "white", borderRadius: "8px", border: "1px solid #e8e8e8", overflow: "hidden" }}>
                    {proposal.recommended_sections?.map((section, idx) => (
                      <div key={idx} style={{
                        padding: "10px 14px",
                        fontSize: "14px",
                        color: "#374151",
                        borderBottom: idx < proposal.recommended_sections.length - 1 ? "1px solid #e8e8e8" : "none",
                        fontFamily: "JetBrains Mono"
                      }}>
                        {section}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Key Requirements */}
                <div>
                  <div style={{ fontSize: "12px", color: "#9ca3af", fontFamily: "JetBrains Mono", marginBottom: "6px" }}>
                    KEY IMPLEMENTATION MANDATES
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {proposal.key_requirements?.map((req, idx) => (
                      <div key={idx} style={{ 
                        display: "flex", 
                        alignItems: "flex-start", 
                        gap: "8px", 
                        background: "#fafafa", 
                        padding: "10px 14px", 
                        borderRadius: "6px", 
                        border: "1px solid #e8e8e8" 
                      }}>
                        <span style={{ color: "#5c9e2e", fontWeight: 600, fontSize: "14px" }}>✓</span>
                        <span style={{ fontSize: "14px", color: "#374151", lineHeight: "1.5", fontFamily: "DM Sans" }}>{req}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Implementation Timeline */}
                <div>
                  <div style={{ fontSize: "12px", color: "#9ca3af", fontFamily: "JetBrains Mono", marginBottom: "6px" }}>
                    EXPECTED ENACTMENT TIMELINE
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {Object.entries(proposal.implementation_timeline || {}).map(([phase, desc]) => (
                      <div key={phase} style={{
                        background: "white",
                        padding: "12px 16px",
                        borderRadius: "8px",
                        border: "1px solid #e8e8e8"
                      }}>
                        <div style={{ color: "#5c9e2e", fontSize: "13px", fontFamily: "JetBrains Mono", fontWeight: 600, marginBottom: "4px" }}>
                          {phase}
                        </div>
                        <div style={{ fontSize: "14px", color: "#374151", fontFamily: "DM Sans", lineHeight: "1.5" }}>
                          {desc}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* NLP Reference Sources */}
                {proposal.reference_policies?.length > 0 && (
                  <div>
                    <div style={{ fontSize: "12px", color: "#9ca3af", fontFamily: "JetBrains Mono", marginBottom: "6px" }}>
                      NLP CROSS-REFERENCE SOURCES
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      {proposal.reference_policies.map((ref, idx) => (
                        <div key={idx} style={{ 
                          display: "flex", 
                          justifyContent: "space-between", 
                          alignItems: "center", 
                          background: "#fafafa", 
                          padding: "8px 12px", 
                          borderRadius: "6px", 
                          border: "1px solid #e8e8e8" 
                        }}>
                          <div>
                            <div style={{ fontSize: "14px", color: "#0a0a0a", fontWeight: 600, fontFamily: "DM Sans" }}>{ref.title}</div>
                            <div style={{ fontSize: "12px", color: "#9ca3af", fontFamily: "DM Sans" }}>Jurisdiction: {ref.country}</div>
                          </div>
                          {ref.source_url && (
                            <a href={ref.source_url} target="_blank" rel="noopener noreferrer" style={{ color: "#5c9e2e", fontSize: "12px", textDecoration: "none", display: "flex", alignItems: "center", gap: "4px", fontFamily: "JetBrains Mono", fontWeight: 600 }}>
                              <ExternalLink size={12} /> LINK
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            )}
          </div>

          {/* Drawer Footer */}
          {proposal && !drawerLoading && (
            <div style={{
              padding: "20px 28px",
              borderTop: "1px solid #e8e8e8",
              background: "white",
              display: "flex"
            }}>
              <button 
                onClick={() => downloadProposalPDF(proposal)}
                style={{
                  flex: 1,
                  padding: "12px",
                  borderRadius: "8px",
                  background: "#0a0a0a",
                  color: "white",
                  fontFamily: "DM Sans",
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: "pointer",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  transition: "background 0.15s ease"
                }}
                onMouseEnter={(e) => e.target.style.background = "#1f2937"}
                onMouseLeave={(e) => e.target.style.background = "#0a0a0a"}
              >
                📥 DOWNLOAD EXECUTIVE PDF PROPOSAL
              </button>
            </div>
          )}

        </div>
      )}

      <PartnerLogos className="partner-logos-footer" showDivider={false} label="powered by" />
    </div>
  );
}